import { useMutation, useQueryClient } from '@tanstack/react-query';
import { withConflictConfirm } from '../availability/conflicts';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { trapTab } from '../ui/focus';
import { packlistKey, type LineView, type PacklistView, type RuleOutcome } from './types';
import { DialogLayer } from '../ui/Dialog';

/**
 * Panel "Reguły" above the packlist: reminders (a message only) and suggestions with "Dodaj" (the rule says what
 * and how many). Nothing to show: no panel.
 */
export function RulePanel({ eventId, suggestions, reminders, canEdit }: { eventId: string; suggestions: RuleOutcome[]; reminders: RuleOutcome[]; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const add = useMutation({
    mutationFn: (ruleId: string) => withConflictConfirm((confirmConflicts) => api<PacklistView>(`/api/packlists/${eventId}/lines`, { method: 'POST', body: { ruleId, ...(confirmConflicts ? { confirmConflicts } : {}) } })),
    onSuccess: (view) => {
      if (view) queryClient.setQueryData(packlistKey(eventId), view);
    },
  });
  if (suggestions.length === 0 && reminders.length === 0) return null;
  return (
    <section className="rule-panel" aria-label="Reguły" data-feature-id="packlists.rules">
      <h3>Reguły</h3>
      {add.error && (
        <p className="alert" role="alert">
          {add.error instanceof ApiError ? add.error.message : 'Nie udało się dodać'}
        </p>
      )}
      <ul className="list">
        {reminders.map((r) => (
          <li key={r.ruleId} className="row">
            <div className="row-main">
              <span className="chip off">przypomnienie</span> <strong>{r.message}</strong>
              <div className="muted">{r.ruleName}</div>
            </div>
          </li>
        ))}
        {suggestions.map((s) => (
          <li key={s.ruleId} className="row">
            <div className="row-main">
              <span className="chip">sugestia</span> <strong>{s.quantity !== undefined ? `${s.quantity} × ${s.itemName}` : s.itemName}</strong>
              <div className="muted">
                {s.ruleName}
                {s.message ? ` — ${s.message}` : ''}
              </div>
            </div>
            {canEdit && (
              <button className="btn small" type="button" aria-label={`Dodaj: ${s.itemName}`} disabled={add.isPending} onClick={() => add.mutate(s.ruleId)}>
                Dodaj
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "Usuń" of a line a required rule keeps: "Ta reguła jest wymagana. Na pewno pominąć?" (no reason asked, owner
 * 2026-10-24 10:44). Every such rule of the line is skipped for this event; what is left of the line (e.g. added by
 * hand) is then removed.
 */
export function SkipRuleDialog({ eventId, line, ruleIds, ruleNames, onClose }: { eventId: string; line: LineView; ruleIds: string[]; ruleNames: Record<string, string>; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const skip = useMutation({
    mutationFn: async () => {
      let view: PacklistView | null = null;
      for (const ruleId of ruleIds) view = await api<PacklistView>(`/api/packlists/lines/${line.id}/rule/${ruleId}`, { method: 'DELETE', body: {} });
      const rest = view?.lines.find((l) => l.id === line.id);
      if (rest) await api(`/api/packlists/lines/${rest.id}?version=${rest.version}`, { method: 'DELETE' });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
      onClose();
    },
    onError: async (e) => {
      setError(e instanceof ApiError ? e.message : 'Nie udało się pominąć reguły');
      await queryClient.invalidateQueries({ queryKey: packlistKey(eventId) });
    },
  });
  const names = ruleIds.map((id) => `„${ruleNames[id] ?? 'reguła'}”`).join(', ');
  return (
    <div className="dialog-backdrop">
      <form
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="skip-rule-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          skip.mutate();
        }}
      >
          <DialogLayer onClose={onClose} busy={skip.isPending} />
        <h2 id="skip-rule-title">Pominąć wymaganą regułę?</h2>
        <p>
          „{line.itemName}” dodaje reguła {names}. Ta reguła jest wymagana. Na pewno pominąć? W tym wydarzeniu nie doda się już sama.
        </p>
        {error && (
          <p className="alert" role="alert">
            {error}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="submit" autoFocus disabled={skip.isPending}>
            {skip.isPending ? 'Pomijanie…' : 'Pomiń regułę'}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </form>
    </div>
  );
}
